import { useCallback, useState } from 'react';
import { useCountries } from '../../../hooks/admin/demographics/useCountries';
import { useZones } from '../../../hooks/admin/demographics/useZones';
import { useRegions } from '../../../hooks/admin/demographics/useRegions';
import { useDistricts } from '../../../hooks/admin/demographics/useDistricts';
import { useWards } from '../../../hooks/admin/demographics/useWards';
import { useVenues } from '../../../hooks/admin/demographics/useVenues';

/**
 * Hook for location selection with configurable level.
 * Selection order: country → zone → region → district → ward → venue.
 * Stops at the specified level (countryLevel, zoneLevel, districtLevel, wardLevel, venueLevel).
 *
 * @param {string} level - 'countryLevel' | 'zoneLevel' | 'districtLevel' | 'wardLevel' | 'venueLevel'
 * @returns {Object} selections, lists (filtered by parent where applicable), setters, loading, error
 */
export function useLocationSelector(level = 'venueLevel') {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedWard, setSelectedWard] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const countryId = selectedCountry?._id ?? selectedCountry?.id ?? null;
  const zoneId = selectedZone?._id ?? selectedZone?.id ?? null;
  const regionId = selectedRegion?._id ?? selectedRegion?.id ?? null;
  const districtId = selectedDistrict?._id ?? selectedDistrict?.id ?? null;
  const wardId = selectedWard?._id ?? selectedWard?.id ?? null;

  // Determine which levels to fetch based on the target level
  const needsCountry = ['countryLevel', 'zoneLevel', 'regionLevel', 'districtLevel', 'wardLevel', 'venueLevel'].includes(level);
  const needsZone = ['zoneLevel', 'regionLevel', 'districtLevel', 'wardLevel', 'venueLevel'].includes(level);
  const needsRegion = ['regionLevel', 'districtLevel', 'wardLevel', 'venueLevel'].includes(level);
  const needsDistrict = ['districtLevel', 'wardLevel', 'venueLevel'].includes(level);
  const needsWard = ['wardLevel', 'venueLevel'].includes(level);
  const needsVenue = level === 'venueLevel';

  const {
    data: countries = [],
    isLoading: loadingCountries,
    error: countriesError,
  } = useCountries({ isActive: true, pageSize: 500 }, { enabled: needsCountry });

  const {
    data: zones = [],
    isLoading: loadingZones,
    error: zonesError,
  } = useZones({ country: countryId, isActive: true, pageSize: 500 }, { enabled: needsZone && !!countryId });

  const {
    data: regions = [],
    isLoading: loadingRegions,
    error: regionsError,
  } = useRegions({ zone: zoneId, country: countryId, isActive: true, pageSize: 500 }, { enabled: needsRegion && !!zoneId });

  const {
    data: districts = [],
    isLoading: loadingDistricts,
    error: districtsError,
  } = useDistricts({ region: regionId, isActive: true, pageSize: 500 }, { enabled: needsDistrict && !!regionId });

  const {
    data: wards = [],
    isLoading: loadingWards,
    error: wardsError,
  } = useWards({ district: districtId, isActive: true, pageSize: 500 }, { enabled: needsWard && !!districtId });

  const {
    data: venues = [],
    isLoading: loadingVenues,
    error: venuesError,
    refetch: refetchVenues,
  } = useVenues(
    { ward: wardId, district: districtId, region: regionId, country: countryId, isActive: true, pageSize: 500 },
    { enabled: needsVenue && !!regionId }
  );

  const loading =
    loadingCountries || loadingZones || loadingRegions || loadingDistricts || loadingWards || loadingVenues;
  const error = countriesError || zonesError || regionsError || districtsError || wardsError || venuesError;

  const resetSelections = useCallback(() => {
    setSelectedCountry(null);
    setSelectedZone(null);
    setSelectedRegion(null);
    setSelectedDistrict(null);
    setSelectedWard(null);
    setSelectedVenue(null);
  }, []);

  const setCountry = (v) => {
    setSelectedCountry(v);
    setSelectedZone(null);
    setSelectedRegion(null);
    setSelectedDistrict(null);
    setSelectedWard(null);
    setSelectedVenue(null);
  };

  const setZone = (v) => {
    setSelectedZone(v);
    setSelectedRegion(null);
    setSelectedDistrict(null);
    setSelectedWard(null);
    setSelectedVenue(null);
  };

  const setRegion = (v) => {
    setSelectedRegion(v);
    setSelectedDistrict(null);
    setSelectedWard(null);
    setSelectedVenue(null);
  };

  const setDistrict = (v) => {
    setSelectedDistrict(v);
    setSelectedWard(null);
    setSelectedVenue(null);
  };

  const setWard = (v) => {
    setSelectedWard(v);
    setSelectedVenue(null);
  };

  const applyVenueSelection = useCallback((venue) => {
    setSelectedVenue(venue ?? null);
    setSelectedCountry(venue?.country ?? null);
    setSelectedZone(null);
    setSelectedRegion(venue?.region ?? selectedRegion ?? null);
    setSelectedDistrict(venue?.district ?? null);
    setSelectedWard(venue?.ward ?? null);
  }, [selectedRegion]);

  return {
    selectedCountry,
    selectedZone,
    selectedRegion,
    selectedDistrict,
    selectedWard,
    selectedVenue,
    setSelectedCountry: setCountry,
    setSelectedZone: setZone,
    setSelectedRegion: setRegion,
    setSelectedDistrict: setDistrict,
    setSelectedWard: setWard,
    setSelectedVenue: setSelectedVenue,
    applyVenueSelection,
    resetSelections,
    countries,
    zones,
    regions,
    districts,
    wards,
    venues,
    refetchVenues,
    loading,
    error: error?.message ?? error ?? null,
    level,
  };
}
